/**
 * HURTIGLEGG — «Ring Hertz i morgen 14 @Thomas #Transport !haster» (KOI-ORDRE §3 Del 1).
 *
 * =================================================================================================
 * EN REN FUNKSJON I `packages/shared`, OG DET ER IKKE EN TILFELDIGHET.
 *
 * Feltet skal vise hva den forsto MENS du skriver — «i morgen 14» blir til en chip med «tors. 17.
 * sep. 14:00» før du trykker Enter — og API-et skal lage nøyaktig den samme oppgaven når Enter
 * kommer. To parsere ville betydd to tolkninger av «neste fredag», og den ene ville vært feil uten
 * at noen kunne se hvilken. Så: én funksjon, i pakken som kjører begge steder.
 *
 * =================================================================================================
 * DEN RETURNERER VEGGKLOKKETID, IKKE ET ØYEBLIKK. Dette er hele grunnen til at `dueAt` her er en
 * streng uten sone.
 *
 * «i morgen 14» betyr klokka to i morgen DER FESTIVALEN ER. Hvilket øyeblikk det er avhenger av
 * `editions.timezone`, og den kjenner ikke nettleseren — en produsent som sitter i London skal få
 * fristen på Oslo-tid, ikke sin egen. Parseren svarer derfor `'2026-09-17T14:00'`, og tjenesten
 * gjør den om til et `timestamptz` i organisasjonens sone. Det er den samme arbeidsdelingen
 * `dueFrom`/`dueTo` i listekallet har.
 *
 * =================================================================================================
 * EN DATO UTEN KLOKKESLETT FORFALLER 23:59, OG DET ER ET VALG.
 *
 * «Ring Hertz på fredag» betyr «i løpet av fredag», ikke «fredag klokka null». Alternativet — et
 * tidspunkt om morgenen — ville gjort oppgaven FORFALT klokka 09:01 på selve dagen, som er den
 * ene tilstanden ingen mener. Lista viser uansett «fre. 18. sep.» og ikke klokkeslettet
 * (`DueDate`), så 23:59 er usynlig helt til noen skriver et klokkeslett selv.
 *
 * =================================================================================================
 * HVA DEN IKKE GJØR: den slår ikke opp noen. `@thomas` kommer tilbake som teksten `thomas`, og det
 * er tjenesten som finner personen — den har basen, RLS-en og retten til å si «den finnes ikke».
 * En parser som slo opp navn måtte hatt tilgang til kontaktlista for å kunne kjøre i det hele tatt,
 * og da kunne den ikke kjørt i nettleseren mens du skriver.
 */
import { z } from 'zod';

import { type TaskPriority, taskPrioritySchema } from '../enums.js';

/** Klokkeslettet en dato uten klokkeslett forfaller på. Se filhodet. */
export const QUICK_TASK_DEFAULT_TIME = '23:59';

/** Hva den lengste hurtigteksten får være. Lengre enn dette er en beskrivelse, ikke en linje. */
export const MAX_QUICK_TASK_TEXT = 500;

/**
 * Et stykke av inndataen parseren spiste, så feltet kan streke under det den forsto.
 *
 * `start`/`end` er indekser i ORIGINALTEKSTEN, ikke i tittelen: tittelen er det som blir igjen når
 * bitene er fjernet, og en understreking må tegnes der ordet faktisk står.
 */
export interface QuickTaskToken {
  kind: 'due' | 'assignee' | 'department' | 'priority';
  /** Teksten slik den ble skrevet, med prefikset («@thomas», «i morgen 14», «!haster»). */
  text: string;
  start: number;
  end: number;
}

export interface QuickTaskDraft {
  /** Det som ble igjen. Aldri tom: en tekst som BARE var tokener beholder originalen. */
  title: string;
  /**
   * Teksten var BARE tokener, og `title` er originalen (K6 L6). «#Transport» ble ellers en oppgave
   * med tittelen «#Transport». Klienten sperrer «Legg til», og `POST /tasks/quick` avviser den.
   */
  untitled: boolean;
  /** Lokal veggklokketid, `YYYY-MM-DDTHH:mm`. Null når ingenting i teksten var en dato. */
  dueAt: string | null;
  /**
   * Teksten inneholdt noe som SÅ UT som en dato, og som ikke er en dag: «32. juli», «31.02.2027».
   *
   * Skilt fra `dueAt === null`, som bare betyr at ingen skrev en dato. Forskjellen er hele poenget:
   * den som ikke skrev en frist skal ikke mases på, og den som skrev en umulig en skal få vite at
   * oppgaven blir laget uten (N1, 20. september).
   */
  unreadableDate: boolean;
  /** Teksten etter `@`, uten oppslag. Tjenesten finner personen. */
  assignee: string | null;
  /** Teksten etter `#`. */
  department: string | null;
  priority: TaskPriority | null;
  tokens: QuickTaskToken[];
}

export const quickTaskTokenSchema = z
  .object({
    kind: z.enum(['due', 'assignee', 'department', 'priority']),
    text: z.string(),
    start: z.number().int().nonnegative(),
    end: z.number().int().nonnegative(),
  })
  .meta({ id: 'QuickTaskToken' });

export const quickTaskDraftSchema = z
  .object({
    title: z.string(),
    untitled: z.boolean(),
    dueAt: z.string().nullable(),
    unreadableDate: z.boolean(),
    assignee: z.string().nullable(),
    department: z.string().nullable(),
    priority: taskPrioritySchema.nullable(),
    tokens: z.array(quickTaskTokenSchema),
  })
  .meta({ id: 'QuickTaskDraft' });

/**
 * Ukedagene, i den rekkefølgen `Date#getDay()` teller dem — søndag først.
 *
 * SKRIVEMÅTER UTEN DIAKRITISKE TEGN STÅR MED VILJE: «lordag» er det folk taster når de har det
 * travelt, og en parser som avviser det gjør et hurtiglegg til en langsom oppgave.
 *
 * FORKORTELSENE STÅR IKKE. «man» er et pronomen på norsk — «man må ringe Hertz» ville fått frist
 * på mandag — og «tor», «lor» og «son» er navn og halve ord. Månedene har forkortelsene sine
 * («15. sep») fordi de bare slår til rett etter et tall, og da kan de ikke være noe annet; en
 * ukedag står fritt i setningen og må derfor være hele ordet.
 */
const WEEKDAYS: Record<string, number> = {
  søndag: 0,
  sondag: 0,
  mandag: 1,
  tirsdag: 2,
  onsdag: 3,
  torsdag: 4,
  fredag: 5,
  lørdag: 6,
  lordag: 6,
};

const MONTHS: Record<string, number> = {
  januar: 1,
  jan: 1,
  februar: 2,
  feb: 2,
  mars: 3,
  mar: 3,
  april: 4,
  apr: 4,
  mai: 5,
  juni: 6,
  jun: 6,
  juli: 7,
  jul: 7,
  august: 8,
  aug: 8,
  september: 9,
  sep: 9,
  sept: 9,
  oktober: 10,
  okt: 10,
  november: 11,
  nov: 11,
  desember: 12,
  des: 12,
};

/** `!haster` og de andre. Norsk i inndataen, engelsk i verdien — som overalt ellers. */
const PRIORITY_WORDS: Record<string, TaskPriority> = {
  haster: 'urgent',
  hast: 'urgent',
  urgent: 'urgent',
  høy: 'high',
  hoy: 'high',
  viktig: 'high',
  high: 'high',
  normal: 'normal',
  lav: 'low',
  low: 'low',
};

/**
 * Datoregning på rene tall, aldri på `Date`.
 *
 * `new Date('2026-09-16')` er midnatt UTC, og `getDate()` på den gir 15. i New York. Hele denne
 * filen handler om veggklokketid, så den regner i år/måned/dag og lar `Date.UTC` gjøre selve
 * kalenderaritmetikken — der er sonen per definisjon irrelevant fordi begge endene er UTC.
 */
interface Ymd {
  year: number;
  month: number;
  day: number;
}

function parseYmd(iso: string): Ymd {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) throw new Error(`Not a date: ${iso}`);
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

function addDays({ year, month, day }: Ymd, days: number): Ymd {
  const moved = new Date(Date.UTC(year, month - 1, day + days));
  return {
    year: moved.getUTCFullYear(),
    month: moved.getUTCMonth() + 1,
    day: moved.getUTCDate(),
  };
}

function weekdayOf({ year, month, day }: Ymd): number {
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * ÅRET PADDES OGSÅ, og det er ikke pynt.
 *
 * `2027-07-01` er en streng `new Date` forstår; `202-07-01` er det ikke — den blir `Invalid Date`,
 * og `Intl` KASTER på en slik, den svarer ikke tomt. Så et tresifret år her ble til at hele sida
 * falt til «Noe gikk galt» hos den som skrev (N1, 20. september). Kilden er tettet i regexen under,
 * men denne funksjonen er den siste som kan gjøre et tall til en dato-streng, og den skal ikke
 * kunne lage en som ikke er en dato.
 */
function toIsoDate({ year, month, day }: Ymd): string {
  return `${String(year).padStart(4, '0')}-${pad(month)}-${pad(day)}`;
}

/**
 * Er dette en dag som finnes? `31. februar` er ikke en frist, det er en skrivefeil, og den skal bli
 * stående i tittelen framfor å bli 3. mars.
 */
function isRealDate({ year, month, day }: Ymd): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const check = new Date(Date.UTC(year, month - 1, day));
  return check.getUTCMonth() === month - 1 && check.getUTCDate() === day;
}

/**
 * ET ÅRSTALL SOM IKKE STÅR I TEKSTEN ER DET FØRSTE SOM IKKE ER I FORTIDA.
 *
 * «15. juli» skrevet i september betyr neste juli, ikke den som var. «20. september» skrevet
 * 16. september betyr om fire dager. Regelen er derfor: i år hvis datoen ikke har vært, ellers
 * neste år — og den er den samme regelen hvert kalenderprodukt bruker, fordi en frist i fortida
 * nesten alltid er en tastefeil og en frist elleve måneder fram nesten alltid ikke er det.
 */
function resolveYear(today: Ymd, month: number, day: number): number {
  const thisYear = { year: today.year, month, day };
  const past =
    thisYear.month < today.month || (thisYear.month === today.month && thisYear.day < today.day);
  return past ? today.year + 1 : today.year;
}

interface DueMatch {
  date: Ymd;
  /** `null` = ingen klokke i teksten; kallet setter `QUICK_TASK_DEFAULT_TIME`. */
  time: string | null;
  start: number;
  end: number;
}

/**
 * Klokkeslett RETT ETTER en dato: «i morgen 14», «fredag 14:30», «15. juli kl. 09».
 *
 * BARE ETTER EN DATO, og det er den viktigste avgrensningen i hele filen. Et fritt tall er ikke et
 * klokkeslett — «Kjøp 14 stoler» skal ikke få frist klokka to — så et bart tall telles bare når det
 * står umiddelbart etter noe som allerede er en dag. «kl 14» uten dato er unntaket som teller
 * likevel, fordi «kl» ikke kan bety noe annet.
 */
const TIME_AFTER_DATE = /^[\s,]*(kl\.?\s*)?(\d{1,2})(?:[:.](\d{2}))?\b/i;
const TIME_WITH_KL = /\bkl\.?\s*(\d{1,2})(?:[:.](\d{2}))?\b/i;

/**
 * ET BART TALL UNDER SEKS ER IKKE ET KLOKKESLETT, og det er den ene heuristikken i filen.
 *
 * «i morgen 14» er klokka to. «Kjøp inn 4 bord i morgen» er fire bord — og uten regelen ville
 * teksten «i morgen 4» blitt til 04:00, som er et tidspunkt ingen setter en frist til. Seks er
 * grensa fordi ingenting i en festivalproduksjon skjer klokka fem om morgenen uten at den som
 * skriver det sier «kl 05» eller «05:00», og begge de formene slipper gjennom: unntaket gjelder
 * bare et tall helt uten både «kl» og minutter.
 */
function readTimeAfter(input: string, from: number): { time: string; end: number } | null {
  const rest = input.slice(from);
  const match = TIME_AFTER_DATE.exec(rest);
  if (!match) return null;
  const hour = Number(match[2]);
  const minute = match[3] === undefined ? 0 : Number(match[3]);
  if (hour > 23 || minute > 59) return null;
  const bare = match[1] === undefined && match[3] === undefined;
  if (bare && hour < 6) return null;
  return { time: `${pad(hour)}:${pad(minute)}`, end: from + match[0].length };
}

/**
 * Datouttrykkene, prøvd i den rekkefølgen de står her.
 *
 * REKKEFØLGEN ER REGELEN: «i overmorgen» må prøves før «i morgen», ellers spiser den siste de fem
 * siste bokstavene og etterlater «i over» i tittelen. Det samme gjelder «neste fredag» før
 * «fredag». Derfor er dette en liste og ikke en veksling i én regulær uttrykk — en liste kan leses
 * ovenfra og ned, og rekkefølgen er synlig.
 */
function matchDue(input: string, today: Ymd, markUnreadable: () => void): DueMatch | null {
  const lower = input.toLowerCase();

  const relative: Array<[RegExp, (m: RegExpExecArray) => Ymd]> = [
    [/\bi\s*overmorgen\b/, () => addDays(today, 2)],
    [/\bovermorgen\b/, () => addDays(today, 2)],
    [/\bi\s*morgen\b/, () => addDays(today, 1)],
    [/\bimorgen\b/, () => addDays(today, 1)],
    [/\bi\s*dag\b/, () => today],
    [/\bidag\b/, () => today],
    [/\bi\s*går\b/, () => addDays(today, -1)],
    [/\bom\s+(\d{1,3})\s+dager?\b/, (m) => addDays(today, Number(m[1]))],
    [/\bom\s+en\s+uke\b/, () => addDays(today, 7)],
    [/\bom\s+(\d{1,2})\s+uker?\b/, (m) => addDays(today, Number(m[1]) * 7)],
    /*
     * «neste uke» er MANDAG i neste uke, ikke «om sju dager». Det er den vanlige meningen, og den
     * eneste som gir samme svar uansett hvilken dag i uka du skriver den.
     */
    [
      /\bneste\s+uke\b/,
      () => addDays(today, (8 - weekdayOf(today)) % 7 || 7),
    ],
  ];

  for (const [pattern, resolve] of relative) {
    const match = pattern.exec(lower);
    if (!match) continue;
    const date = resolve(match);
    const afterDate = match.index + match[0].length;
    const time = readTimeAfter(input, afterDate);
    return {
      date,
      time: time?.time ?? null,
      start: match.index,
      end: time?.end ?? afterDate,
    };
  }

  // «neste fredag», «fredag», «fre» — den nærmeste forekomsten, pluss en uke når det står «neste».
  const weekdayPattern = new RegExp(`\\b(neste\\s+)?(${Object.keys(WEEKDAYS).join('|')})\\b`);
  const weekday = weekdayPattern.exec(lower);
  if (weekday) {
    const target = WEEKDAYS[weekday[2] as string];
    if (target !== undefined) {
      const ahead = (target - weekdayOf(today) + 7) % 7;
      const date = addDays(today, ahead + (weekday[1] ? 7 : 0));
      const afterDate = weekday.index + weekday[0].length;
      const time = readTimeAfter(input, afterDate);
      return {
        date,
        time: time?.time ?? null,
        start: weekday.index,
        end: time?.end ?? afterDate,
      };
    }
  }

  /*
   * EN DATOFORM SOM IKKE ER EN DATO SKAL SIES HØYT (N1, 20. september).
   *
   * De tre neste formene kan alle TREFFE og likevel navngi en dag som ikke finnes: «32. juli»,
   * «2027-02-31», «31.02.2027». Til nå falt de stille gjennom til `null`, og oppgaven ble laget
   * uten frist uten at noen fikk vite det — verre enn å nekte, fordi den som skrev en frist tror
   * den er der. `unreadableDate` er det feltet sier fra om.
   */
  // «15. juli», «15 juli 2027», «15. jul»
  const named = new RegExp(
    `\\b(\\d{1,2})\\.?\\s*(${Object.keys(MONTHS).join('|')})\\.?(\\s+(\\d{4}))?\\b`,
  ).exec(lower);
  if (named) {
    const day = Number(named[1]);
    const month = MONTHS[named[2] as string] as number;
    const year = named[4] ? Number(named[4]) : resolveYear(today, month, day);
    const date = { year, month, day };
    if (isRealDate(date)) {
      const afterDate = named.index + named[0].length;
      const time = readTimeAfter(input, afterDate);
      return { date, time: time?.time ?? null, start: named.index, end: time?.end ?? afterDate };
    }
    markUnreadable();
  }

  // ISO: «2027-07-15», som er den ene formen ingen kan misforstå.
  const iso = /\b(\d{4})-(\d{2})-(\d{2})\b/.exec(lower);
  if (iso) {
    const date = { year: Number(iso[1]), month: Number(iso[2]), day: Number(iso[3]) };
    if (isRealDate(date)) {
      const afterDate = iso.index + iso[0].length;
      const time = readTimeAfter(input, afterDate);
      return { date, time: time?.time ?? null, start: iso.index, end: time?.end ?? afterDate };
    }
    markUnreadable();
  }

  /*
   * «15/7», «15.7», «15/7-2027», «15.07.2027» — DAG FØRST, ALLTID.
   *
   * Norsk skriver dag før måned, og en parser som gjettet på amerikansk rekkefølge ville gjort
   * «7/8» til 7. august for den ene og 8. juli for den andre. Punktum-varianten krever begge
   * tallene med minst ett siffer og godtar ikke «15.» alene: et tall med punktum bak seg er en
   * nummerering langt oftere enn en dato.
   *
   * ÅRET ER TO SIFFER ELLER FIRE, ALDRI TRE (N1, 20. september).
   *
   * `\d{2,4}` godtok tre, og tre siffer finnes hver eneste gang noen skriver et fire-sifret år:
   * «1.7.2027» går gjennom «1.7.202» på vei dit. Året ble 202, datoen ble strengen `202-07-01`, og
   * den er ikke en dato — forhåndsvisningen formaterte den, `Intl` kastet, og sida falt MENS MAN
   * SKREV. Uten den mellomtilstanden hadde ingen sett feilen; med den ser alle den, hver gang.
   *
   * Nå faller det halve årstallet ut av treffet i stedet, og «1.7.202» tolkes som «1.7» med året
   * utledet av regelen under — samme svar som det ferdigskrevne «1.7.2027» gir. Ingen krasj, og
   * ingen forhåndsvisning som spretter til et annet år og tilbake mens man taster.
   */
  const numeric = /\b(\d{1,2})[./](\d{1,2})(?:[./-](\d{2}|\d{4}))?\b/.exec(lower);
  if (numeric) {
    const day = Number(numeric[1]);
    const month = Number(numeric[2]);
    let year: number;
    if (numeric[3] === undefined) {
      year = resolveYear(today, month, day);
    } else {
      const raw = Number(numeric[3]);
      year = raw < 100 ? 2000 + raw : raw;
    }
    const date = { year, month, day };
    if (isRealDate(date)) {
      const afterDate = numeric.index + numeric[0].length;
      const time = readTimeAfter(input, afterDate);
      return { date, time: time?.time ?? null, start: numeric.index, end: time?.end ?? afterDate };
    }
    markUnreadable();
  }

  // «kl 14» helt alene betyr i dag klokka 14 — «kl» kan ikke bety noe annet.
  const bareTime = TIME_WITH_KL.exec(input);
  if (bareTime) {
    const hour = Number(bareTime[1]);
    const minute = bareTime[2] === undefined ? 0 : Number(bareTime[2]);
    if (hour <= 23 && minute <= 59) {
      return {
        date: today,
        time: `${pad(hour)}:${pad(minute)}`,
        start: bareTime.index,
        end: bareTime.index + bareTime[0].length,
      };
    }
  }

  return null;
}

/**
 * `matchDue` med svaret på det andre spørsmålet ved siden av: SÅ DETTE UT SOM EN DATO?
 *
 * To spørsmål og ett gjennomløp, fordi de deler hele regningen: «31.02.2027» treffer det samme
 * mønsteret som «15.07.2027» og skiller seg fra den først i `isRealDate`. Å svare på det andre
 * spørsmålet i en egen funksjon ville vært en andre kopi av mønstrene, og den kopien ville blitt
 * uenig med den første den dagen noen la til en form.
 */
function findDue(input: string, today: Ymd): { due: DueMatch | null; unreadableDate: boolean } {
  let unreadable = false;
  const due = matchDue(input, today, () => {
    unreadable = true;
  });
  /* En dato som BLE forstått er ikke uleselig, uansett hva et tidligere mønster mente om den. */
  return { due, unreadableDate: due === null && unreadable };
}

/**
 * `@navn` og `#avdeling`. Ett ord, og bokstavene er unicode: «#økonomi» og «@bjørn» er vanligere
 * her enn de engelske variantene.
 *
 * FLERE ORD STØTTES MED ANFØRSELSTEGN — `@"Anders Aas"` — fordi et etternavn ikke er et nytt ord i
 * setningen, og alternativet (å gjette på at neste ord med stor bokstav hører til) ville spist
 * «Ring @Thomas Hertz» sitt siste ord.
 */
const HANDLE = /(^|\s)([@#!])(?:"([^"]{1,60})"|([\p{L}\p{N}_.-]{1,60}))/gu;

/**
 * Del en hurtigtekst i det den betyr.
 *
 * `today` er DAGENS DATO I ORGANISASJONENS SONE som `YYYY-MM-DD` — kalleren vet hvilken sone det
 * er, parseren gjør ikke. Se filhodet.
 */
export function parseQuickTask(input: string, options: { today: string }): QuickTaskDraft {
  const today = parseYmd(options.today);
  const text = input.slice(0, MAX_QUICK_TASK_TEXT);

  const tokens: QuickTaskToken[] = [];
  let assignee: string | null = null;
  let department: string | null = null;
  let priority: TaskPriority | null = null;

  HANDLE.lastIndex = 0;
  let handle: RegExpExecArray | null = HANDLE.exec(text);
  while (handle !== null) {
    const lead = handle[1] ?? '';
    const sigil = handle[2] as '@' | '#' | '!';
    const value = handle[3] ?? handle[4] ?? '';
    const start = handle.index + lead.length;
    const end = handle.index + handle[0].length;

    if (sigil === '@' && assignee === null) {
      assignee = value;
      tokens.push({ kind: 'assignee', text: text.slice(start, end), start, end });
    } else if (sigil === '#' && department === null) {
      department = value;
      tokens.push({ kind: 'department', text: text.slice(start, end), start, end });
    } else if (sigil === '!') {
      const found = PRIORITY_WORDS[value.toLowerCase()];
      // `!` foran noe som ikke er en prioritet er et utropstegn. Det blir staaende i tittelen.
      if (found !== undefined && priority === null) {
        priority = found;
        tokens.push({ kind: 'priority', text: text.slice(start, end), start, end });
      }
    }
    handle = HANDLE.exec(text);
  }

  /*
   * DATOEN LETES ETTER I TEKSTEN UTEN HÅNDTAKENE, og det er ikke pynt: «@fredag» er et navn og ikke
   * en dag, og «#15/7» er en avdeling som heter noe rart. Håndtakene erstattes med mellomrom av
   * samme lengde, så alle indeksene fortsatt peker på originalteksten.
   */
  let masked = text;
  for (const token of tokens) {
    masked =
      masked.slice(0, token.start) + ' '.repeat(token.end - token.start) + masked.slice(token.end);
  }

  const { due, unreadableDate } = findDue(masked, today);
  if (due) {
    tokens.push({
      kind: 'due',
      text: text.slice(due.start, due.end).trim(),
      start: due.start,
      end: due.end,
    });
  }

  // Tittelen er det som blir igjen. Bitene fjernes bakfra, saa indeksene holder seg gyldige.
  const ordered = [...tokens].sort((a, b) => b.start - a.start);
  let title = text;
  for (const token of ordered) {
    title = title.slice(0, token.start) + title.slice(token.end);
  }
  /*
   * Opprydningen etter at bitene er tatt ut: dobbelt mellomrom blir ett, og et skilletegn som ble
   * hengende etter et fjernet ord flyttes inntil ordet foran. `(?=\s|$)` er ikke pynt — uten den
   * spiser regelen mellomrommet foran «!viktigst» og gjør «Husk dette !viktigst» til ett ord.
   */
  title = title
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?])(?=\s|$)/g, '$1')
    .trim();

  /*
   * EN TEKST SOM BARE VAR TOKENER BEHOLDER ORIGINALEN. «@thomas i morgen» uten noe mer er en
   * oppgave uten tittel, og en tom tittel avvises av databasen — så den får teksten sin tilbake og
   * personen retter den. Å finne på en tittel ville vært verre enn å vise hva de skrev.
   */
  const untitled = title.length === 0;
  if (untitled) title = text.trim();

  return {
    title,
    untitled,
    dueAt: due ? `${toIsoDate(due.date)}T${due.time ?? QUICK_TASK_DEFAULT_TIME}` : null,
    unreadableDate,
    assignee,
    department,
    priority,
    tokens: tokens.sort((a, b) => a.start - b.start),
  };
}
